import React from 'react';
import { amountToWordsSoles } from '../../utils/amountToWords';
import {
  formatPrintDate,
  formatPrintDateOrDash,
  resolveOrderPaymentPrintDate,
} from '../../utils/dateUtils';

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

const formatMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function buildPaymentCondition(quote) {
  if (quote.payment_condition_type === 'cash') return 'Al contado';
  if (quote.payment_condition_type === 'days') {
    return `Cada ${quote.payment_condition_days} días`;
  }
  if (quote.payment_condition_type === 'date' && quote.payment_condition_date) {
    return `Fecha específica: ${formatPrintDate(quote.payment_condition_date)}`;
  }
  return '-';
}

function docTypeLabel(client) {
  if (client?.document_type === 'ruc') return 'RUC';
  if (client?.document_type === 'dni') return 'DNI';
  if (client?.document_type) return String(client.document_type).toUpperCase();
  return 'Documento';
}

function getItemDescription(item) {
  if (item.item_type === 'service' || item.service_id) {
    return item.service?.name || item.description || 'Servicio';
  }
  return item.product?.name || item.description || 'Producto';
}

const SalesQuotePrintDocument = ({ quote }) => {
  if (!quote) return null;

  const statusTone = quote.status === 'cancelled' ? 'cancelled' : 'issued';
  const statusLabel = quote.status === 'cancelled' ? 'Cancelada' : 'Emitida';
  const paymentDateNumeric = resolveOrderPaymentPrintDate(quote);
  const igvRatePercent = parseFloat(quote.igv_rate ?? 18);

  const scheduleParts = [];
  if (quote.delivery_date) scheduleParts.push(`Entrega: ${formatPrintDate(quote.delivery_date)}`);
  if (quote.service_start_date) scheduleParts.push(`Inicio: ${formatPrintDate(quote.service_start_date)}`);
  if (quote.service_end_date) scheduleParts.push(`Fin: ${formatPrintDate(quote.service_end_date)}`);

  const posLabel = quote.point_of_sale
    ? [quote.point_of_sale.code, quote.point_of_sale.name].filter(Boolean).join(' — ')
    : '—';

  const items = quote.items || [];

  return (
    <div className="print-container">
      <h1 className="doc-title">Cotización</h1>
      <hr className="doc-title-rule" />

      <div className="info-line">
        <span className="info-label">Cotización:</span> {quote.code || '—'}
        <span className="info-sep">·</span>
        <span className="info-label">Fecha:</span> {formatPrintDateOrDash(quote.issue_date)}
        <span className="info-sep">·</span>
        <span className="info-label">Válida hasta:</span>{' '}
        {quote.valid_until ? formatPrintDateOrDash(quote.valid_until) : '—'}
        <span className="info-sep">·</span>
        <span className="info-label">Estado:</span>{' '}
        <span className={`status-badge status-${statusTone}`}>{statusLabel}</span>
      </div>

      <div className="info-line">
        <span className="info-label">Cliente:</span> {quote.client?.name || 'N/A'}
        {quote.client?.document_number && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">{docTypeLabel(quote.client)}:</span> {quote.client.document_number}
          </>
        )}
      </div>

      <div className="info-line">
        <span className="info-label">Punto de venta:</span> {posLabel}
      </div>

      <div className="info-line">
        <span className="info-label">Condición de pago:</span> {buildPaymentCondition(quote)}
        {paymentDateNumeric && quote.payment_condition_type !== 'cash' && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">Fecha de pago:</span> {paymentDateNumeric}
          </>
        )}
      </div>

      {scheduleParts.length > 0 && (
        <div className="info-line">
          <span className="info-label">Plazos:</span> {scheduleParts.join(' · ')}
        </div>
      )}

      <div className="items-section">
        <div className="section-title">Items de la cotización</div>

        <div className="items-table-wrap">
          <table className="items-table cols-6">
            <thead>
              <tr>
                <th>Descripción</th>
                <th className="center">Unidad</th>
                <th>Proyecto</th>
                <th className="num">Cant.</th>
                <th className="num">P. Unit</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6}>Sin ítems registrados</td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td>{getItemDescription(item)}</td>
                    <td className="center">
                      {item.unit_of_measure ||
                        item.product?.unit_of_measure ||
                        item.service?.unit_of_measure ||
                        'UND'}
                    </td>
                    <td>
                      {item.project
                        ? `${item.project.code || ''}${item.project.code && item.project.name ? ' - ' : ''}${item.project.name || ''}`
                        : '—'}
                    </td>
                    <td className="num">{formatQty(item.quantity)}</td>
                    <td className="num">S/ {formatUnitPrice(item.unit_price)}</td>
                    <td className="subtotal-cell">S/ {formatMoney(item.subtotal ?? item.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <div className="print-footer-obs">
            <div className="print-obs-label">Observaciones:</div>
            {quote.notes ? <div className="print-obs-text">{quote.notes}</div> : null}
          </div>
          <div className="print-footer-total">
            <div className="print-total-row">
              <span className="print-total-label">Base:</span>
              <span className="print-total-value">S/ {formatMoney(quote.base_imponible)}</span>
            </div>
            <div className="print-total-row">
              <span className="print-total-label">IGV ({igvRatePercent}%):</span>
              <span className="print-total-value">S/ {formatMoney(quote.igv_amount)}</span>
            </div>
            {parseFloat(quote.no_gravados || 0) > 0 && (
              <div className="print-total-row">
                <span className="print-total-label">No grav.:</span>
                <span className="print-total-value">S/ {formatMoney(quote.no_gravados)}</span>
              </div>
            )}
            <div className="print-total-row print-total-row-final">
              <span className="print-total-label">Total:</span>
              <span className="print-total-value">S/ {formatMoney(quote.total_amount)}</span>
            </div>
            <div className="print-totals-words">Son: {amountToWordsSoles(quote.total_amount)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesQuotePrintDocument;
